// Rewritten from Dealtracker src/appframe/tours/Confetti.tsx (commit 30ca0cb): a plain portal
// instead of Fluent's, the keyframes in styles.css (`.tour-confetti`).
import { useEffect, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'

/** How long the glitter falls (≈1.8 s). */
export const CONFETTI_MS = 1800
const PIECES = 40
// Decorative only (aria-hidden), so exempt from contrast; the same colours in every scheme.
const COLORS = ['#0f6cbd', '#e3008c', '#ffb900', '#13a10e', '#8764b8', '#ff8c00']

export function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

// A fixed, seeded layout: the same burst every time, nothing random to test around.
const pieces = Array.from({ length: PIECES }, (_, i) => {
  const r = (n: number) => {
    const x = Math.sin((i + 1) * 12.9898 + n * 78.233) * 43758.5453
    return x - Math.floor(x)
  }
  return {
    left: `${(r(1) * 100).toFixed(1)}%`,
    w: 6 + Math.round(r(2) * 6),
    h: 8 + Math.round(r(3) * 8),
    color: COLORS[i % COLORS.length],
    vars: {
      '--drift': `${Math.round((r(4) - 0.5) * 160)}px`,
      '--turn': `${Math.round(180 + r(5) * 540)}deg`,
      '--dur': `${Math.round(CONFETTI_MS * (0.7 + r(6) * 0.3))}ms`,
      '--delay': `${Math.round(r(7) * 250)}ms`,
    },
  }
})

/**
 * The congratulations card's glitter: a short CSS-only burst over the whole page, purely
 * decorative — `aria-hidden`, `pointer-events: none`, under the card, gone after
 * {@link CONFETTI_MS}. Each piece falls and turns once (no blinking, WCAG 2.3.1). Nothing at all
 * under reduced motion.
 */
export function Confetti() {
  const [on, setOn] = useState(() => !prefersReducedMotion())
  useEffect(() => {
    if (!on) return
    const t = setTimeout(() => setOn(false), CONFETTI_MS + 400)
    return () => clearTimeout(t)
  }, [on])
  if (!on) return null
  return createPortal(
    <div className="tour-confetti" data-testid="tour-confetti" data-tour-window="" aria-hidden="true">
      {pieces.map((p, i) => (
        <span
          key={i}
          className="tour-confetti-piece"
          style={{ left: p.left, width: p.w, height: p.h, background: p.color, ...(p.vars as CSSProperties) }}
        />
      ))}
    </div>,
    document.body,
  )
}
